import { observer } from "mobx-react";
import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
// Source
import { NeededFiles, TableTransformParam, WorkbookParams } from "../../interface";
import { getUploadList } from "../../actions/getUploadList";
import { getWorkbook } from "../../actions/getWorkbook";
import { uploadFile } from "../../actions/uploadFile";
import { processFile } from "../../actions/processFile";
import { lang } from "../../lang";
import { Workbook } from "../../interface";
import { store } from "../../store";
import { UPLOAD_POPUP_TYPE, uploadValidator, pad, setLastActivity } from "../../HelperSrc";
import { Button } from "../../components/atoms/Button";
import { CenteredContent } from "../../components/atoms/CenteredContent";
import { FlexHorizontal, FlexSpacer } from "../../components/atoms/Flex";
import { Title, SubText } from "../../components/atoms/Text";
import { Popup } from "../../components/molecules/Popup";
import { Table } from "../../components/molecules/Table";
import { Template } from "../../components/organisms/Template";
import { catchEndpoint } from "../../catchEndpoint";

@observer
class WorkbookFilesPageBase extends React.Component<RouteComponentProps<WorkbookParams>> {
    public processTimer: NodeJS.Timeout | null = null;
    public inputElement: HTMLInputElement | null = null;
    public state = {
        neededFiles: [] as NeededFiles[],
        workbook: null as unknown as Workbook,
        uploadPopup: 0,
        workbookLoading: false,
        statusLoading: true
    };
    public isStartProcess = false;
    public isStartUpload = false;

    public async componentDidMount() {
        const { match } = this.props;
        const { workbookid } = match.params;

        store.setToUpload([]);
        this.setState({
            workbookLoading: true
        });
        const workbook = await getWorkbook(Number(workbookid));
        const neededFiles = await getUploadList(workbookid);
        this.setState({ workbookLoading: false, workbook, neededFiles });

        const inp = (this.inputElement = document.createElement("input"));
        inp.type = "file";
        inp.setAttribute("id", "file_input");
        inp.setAttribute("style", "display: none;");
        inp.multiple = true;
        inp.onchange = async () => {
            const files = inp.files ? Array.from(inp.files) : [];
            let validUpload = true;
            store.toUpload = files.map((file) => {
                validUpload = validUpload && uploadValidator(file.name, this.state.neededFiles);
                return {
                    file,
                    name: file.name,
                    status: lang.uploaded
                };
            });
            if (!validUpload) {
                this.setState({ uploadPopup: UPLOAD_POPUP_TYPE.ERROR });

                if (this.inputElement) {
                    this.inputElement.value = "";
                }

                return;
            }

            this.isStartUpload = true;

            const failedList: Record<string, string[]> = {};

            for (const up of store.toUpload) {
                up.status = lang.uploading;
                store.setToUpload([...store.toUpload]);
                const errorMsg = await uploadFile(up.file, workbookid);

                if (errorMsg === "") {
                    up.status = lang.done;
                } else {
                    up.status = lang.failed;

                    if (!(errorMsg in failedList)) {
                        failedList[errorMsg] = [];
                    }

                    failedList[errorMsg].push(up.file.name);
                }

                store.setToUpload([...store.toUpload]);
            }

            for (const [msg, fileList] of Object.entries(failedList)) {
                if (msg === "unsupportedFileType") {
                    store.doNotice(lang[msg], false, 3000, () => {}, fileList);
                } else {
                    store.doNotice(lang[msg]);
                }
            }

            this.setState({
                statusLoading: true,
                uploadPopup: UPLOAD_POPUP_TYPE.FINISHED
            });
            this.isStartUpload = false;

            setLastActivity();
        };
        document.body.appendChild(inp);

        this.processTimer = setInterval(this.updateProcessing, 4000);
    }

    public componentWillUnmount() {
        store.setToUpload([]);

        const element = document.querySelector("#file_input") as HTMLInputElement;
        if (element) document.body.removeChild(element);

        if (this.processTimer) clearInterval(this.processTimer);
    }

    public updateProcessing = () => {
        const { match } = this.props;
        const { workbookid } = match.params;
        if (this.state.statusLoading && store.user.id) {
            getUploadList(workbookid)
                .then((neededFiles) => {
                    this.setState({ neededFiles });

                    const fileList: string[] = [];
                    store.toUpload.forEach((file) => {
                        fileList.push(file.file.name);
                    });
                    let isDownloaded = true;
                    neededFiles?.every((file: NeededFiles) => {
                        if (fileList.includes(file.file_name)) {
                            isDownloaded = isDownloaded && file.file_status == "DONE";
                        } else if (file.file_status == "PROCESSING") {
                            isDownloaded = false;
                            return false;
                        }

                        const trList = document.querySelectorAll<HTMLElement>(".row");
                        if (file.file_status == "DONE") {
                            for (let a = 0; a < trList.length; a++) {
                                const neededFileName = trList[a].querySelector<HTMLElement>(".needed_file_name");
                                if (neededFileName && neededFileName.innerText === file.file_name) {
                                    trList[a].classList.add("status_done");
                                }
                            }
                        } else if (file.file_status == "UPLOADED") {
                            for (let a = 0; a < trList.length; a++) {
                                const neededFileName = trList[a].querySelector<HTMLElement>(".needed_file_name");
                                if (neededFileName && neededFileName.innerText === file.file_name) {
                                    trList[a].classList.add("status_upload_done");
                                }
                            }
                        }

                        return true;
                    });
                    this.setState({
                        statusLoading: !isDownloaded
                    });

                    setLastActivity();
                })
                .catch(catchEndpoint);
        }
    };

    public processFile = async (filetype: string) => {
        const { match } = this.props;
        const { workbookid } = match.params;

        this.isStartProcess = true;

        for (const file of this.state.neededFiles) {
            if (file.upload_file_name.toLowerCase().endsWith(filetype) && file.file_status === "UPLOADED") {
                await processFile(file.upload_file_name, workbookid);
            }
        }

        this.setState({
            statusLoading: true
        });
        this.isStartProcess = false;
    };

    public openFileChooser = () => {
        if (this.inputElement) this.inputElement.click();
    };

    public closePopup = () => {
        this.setState({
            uploadPopup: UPLOAD_POPUP_TYPE.HIDDEN,
            statusLoading: true
        });
        if (this.state.uploadPopup === UPLOAD_POPUP_TYPE.FINISHED && this.inputElement) {
            this.inputElement.value = "";
        }
    };

    public render() {
        const { neededFiles, workbook } = this.state;

        let allXlsxUploaded = true;
        let hasCsvUploaded = false;
        let hasPdfUploaded = false;
        let hasProcessing = false;

        let xlsxCount = 0;

        for (const file of neededFiles) {
            if (file.file_name.toLowerCase().endsWith("xlsx") && file.file_status !== "UPLOADED") {
                xlsxCount += 1;

                if (xlsxCount > 2) {
                    allXlsxUploaded = false;
                }
            }

            if (file.upload_file_name.toLowerCase().endsWith("csv") && file.file_status === "UPLOADED") {
                hasCsvUploaded = true;
            }

            if (file.upload_file_name.toLowerCase().endsWith("pdf") && file.file_status === "UPLOADED") {
                hasPdfUploaded = true;
            }

            if (file.file_status === "PROCESSING") {
                hasProcessing = true;
            }
        }

        return (
            <>
                <Template
                    title={workbook && `${workbook.name || ""} (${workbook.year || ""})`}
                    wbId={workbook && `${workbook.id || ""}`}
                    locked={workbook && workbook.is_locked}
                >
                    <CenteredContent>
                        <FlexHorizontal>
                            <Title>{lang.requiredFiles}</Title>
                            <FlexSpacer />
                            {(() => {
                                const html: JSX.Element[] = [];

                                if ((store.permission === "CONTROL" || store.permission === "WORK") && workbook && !workbook.is_locked) {
                                    if (allXlsxUploaded) {
                                        html.push(
                                            <div className="container_action">
                                                <Button
                                                    loading={this.isStartProcess}
                                                    data-at_id="process_xlsx_button"
                                                    title={lang.processXlsx}
                                                    onClick={() => {
                                                        this.processFile("xlsx");
                                                    }}
                                                />
                                            </div>
                                        );
                                    } else {
                                        html.push(
                                            <div className="container_action">
                                                {(() => {
                                                    const html: JSX.Element[] = [];

                                                    if (!hasProcessing) {
                                                        html.push(
                                                            <Button
                                                                loading={this.isStartUpload}
                                                                data-at_id="upload_button"
                                                                title={lang.upload}
                                                                onClick={this.openFileChooser}
                                                            />
                                                        );
                                                    }

                                                    if (hasCsvUploaded) {
                                                        html.push(
                                                            <Button
                                                                loading={this.isStartProcess}
                                                                data-at_id="process_csv_button"
                                                                title={lang.processCsv}
                                                                onClick={() => {
                                                                    this.processFile("csv");
                                                                }}
                                                            />
                                                        );
                                                    } else if (hasPdfUploaded) {
                                                        html.push(
                                                            <Button
                                                                loading={this.isStartProcess}
                                                                data-at_id="process_pdf_button"
                                                                title={lang.processPdf}
                                                                onClick={() => {
                                                                    this.processFile("pdf");
                                                                }}
                                                            />
                                                        );
                                                    }

                                                    return html;
                                                })()}
                                            </div>
                                        );
                                    }
                                }

                                return html;
                            })()}
                        </FlexHorizontal>
                        <div className="upload_instruction">
                            <SubText>1. {lang.uploadExcel}</SubText>
                            <SubText>2. {lang.uploadCSV}</SubText>
                            <SubText>3. {lang.uploadPDF}</SubText>
                        </div>
                        <table className="upload_table" data-at_id="upload_table">
                            <tbody>
                                <tr style={{ borderBottom: "none" }}>
                                    <td style={{ padding: 0, lineHeight: 0 }}>
                                        <Table
                                            className="needed_files_table"
                                            loading={this.state.workbookLoading}
                                            data={neededFiles}
                                            columnNames={neededFileColumns}
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </CenteredContent>
                </Template>

                {this.state.uploadPopup === UPLOAD_POPUP_TYPE.ERROR ? (
                    <div className="upload_error_popup">
                        <Popup onClose={this.closePopup}>
                            <Title className="text_center">{lang.wrongUploadOrder}</Title>
                            <div className="popup_text_container">
                                <SubText>- {lang.uploadExcel}</SubText>
                                <SubText>- {lang.uploadCSV}</SubText>
                                <SubText>- {lang.uploadPDF}</SubText>
                            </div>
                            <div className="popup_button_container">
                                <Button title={lang.close} onClick={this.closePopup} />
                            </div>
                        </Popup>
                    </div>
                ) : null}
            </>
        );
    }
}

const neededFileColumns = [
    { id: "file_name", name: lang.fileName, dataAtId: "needed_file_name" },
    {
        id: "upload_date",
        name: lang.date,
        transform: (e: TableTransformParam) => {
            const dt = new Date(String(e));
            return `${dt.getFullYear()}-${pad(String(dt.getMonth() + 1))}-${pad(String(dt.getDate()))} ${pad(String(dt.getHours()))}:${pad(
                String(dt.getMinutes())
            )}`;
        },
        width: "180px"
    },
    { id: "uploader", name: lang.user },
    {
        id: "file_status",
        name: lang.status,
        transform: (e: TableTransformParam) => String(lang[String(e).toLowerCase()]) || String(e).toLowerCase(),
        nowrap: true,
        dataAtId: "needed_file_status"
    }
];

const WorkbookFilesPage = withRouter(WorkbookFilesPageBase);

export { WorkbookFilesPage };
